import { Settings } from './settings';
import { prepareDate } from './helpers';

export interface IFeatures {
  plan: string;
  role: string;
}

export interface ISubscription {
  billing_type: string;
  cancel_url: URL | null;
  currency: string;
  event_time: string;
  next_bill_date: string;
  plan_name: string;
  source: string;
  status: string;
  unit_price: number | null;
  update_url: string | null;
}

export class User {
  id: string;
  type = 'users';
  email: string;
  name: string;
  first_name: string;
  last_name: string;
  avatar: string;
  features: IFeatures = null;
  settings: Settings;
  created: string;
  updated: string;
  subscriptions: ISubscription[];
  actingAs: string = null;
  is_staff = 'False';

  constructor({
    id = null,
    email = '',
    name = '',
    first_name = '',
    last_name = '',
    avatar = '',
    created = null,
    updated = null,
    features = null,
    settings = null,
    subscriptions = [],
    actingAs = null,
    is_staff = 'False',
    ...rest
  }: {
    id?: string;
    email?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    avatar?: string;
    layoutFavorites?: string[];
    mapFavorites?: string[];
    hasSeenTutorial?: boolean;
    created?: string;
    updated?: string;
    features?: IFeatures;
    settings?: Settings;
    subscriptions?: ISubscription[];
    actingAs?: string;
    is_staff?: string;
  }) {
    const now = new Date();
    this.id = id;
    this.email = email;
    this.name = name;
    this.first_name = first_name;
    this.last_name = last_name;
    this.avatar = avatar;
    this.features = features || { plan: 'Starters', role: 'User' };
    this.created = prepareDate(created, now);
    this.updated = prepareDate(updated, now);
    if (!settings) {
      settings = new Settings({});
    } else {
      this.settings = new Settings(settings);
    }
    this.subscriptions = subscriptions;
    this.actingAs = actingAs;
    this.is_staff = is_staff;
    Object.assign(this, rest); // allow old source to edit/persist properties from future versions
  }

  isBetaTester(): boolean {
    return this.features.plan === 'Masters';
  }

  isPro(): boolean {
    return this.features?.plan === 'Pro';
  }

  isMasters(): boolean {
    return (
      this.isBetaTester() ||
      this.isPro() ||
      this.activeSubscriptions().filter((sub) =>
        sub.plan_name.toLowerCase().includes('masters'),
      ).length > 0
    );
  }

  activeSubscriptions(): ISubscription[] {
    return this.subscriptions.filter((sub) => sub.status !== 'deleted');
  }

  hasActiveSubscription(): boolean {
    return this.activeSubscriptions().length > 0;
  }

  get isStaff(): boolean {
    return this.is_staff === 'True';
  }

  username(): string {
    return this.first_name + ' ' + this.last_name;
  }
}
